import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { Package, Users, ClipboardList, Megaphone, BarChart3, TrendingUp, User, LogOut, ChevronDown, UserPlus, Menu, X, UserCircle, LayoutDashboard, Bell } from 'lucide-react';
import NotificationBell from './NotificationBell';
import ConfirmModal from './ConfirmModal';
import './AdminHeader.css';

const AdminHeader = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isSuperAdmin } = useAuth();
  const { addToast } = useToast();
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const dropdownRef = useRef(null);
  const profileButtonRef = useRef(null);
  const mobileMenuRef = useRef(null);

  const handleLogout = async () => {
    setShowProfileDropdown(false);
    setShowLogoutModal(true);
  };

  const confirmLogout = async () => {
    await logout();
    addToast({
      type: 'success',
      message: 'Logged out successfully!'
    });
    navigate('/');
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!showProfileDropdown) return;

    const handleClickOutside = (event) => {
      // Don't close if clicking on the button - let button's onClick handle toggle
      if (profileButtonRef.current?.contains(event.target)) {
        return;
      }

      // Don't close if clicking inside the dropdown
      if (dropdownRef.current?.contains(event.target)) {
        return;
      }

      // Click is outside, close dropdown
      setShowProfileDropdown(false);
    };

    // Add listener with a small delay to avoid immediate closure
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside, true);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [showProfileDropdown]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target) && !event.target.closest('.mobile-menu-toggle')) {
        setShowMobileMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setShowMobileMenu(false);
  }, [location.pathname]);

  // Close profile dropdown when route changes
  useEffect(() => {
    setShowProfileDropdown(false);
  }, [location.pathname]);

  return (
    <header className="admin-header">
      <button 
        className="mobile-menu-toggle"
        onClick={() => setShowMobileMenu(!showMobileMenu)}
        aria-label="Toggle menu"
      >
        {showMobileMenu ? <X size={24} /> : <Menu size={24} />}
      </button>

      <div className="admin-header-main">
        <div className="admin-title">
          <h1>{isSuperAdmin() ? 'Super admin' : 'Admin'}</h1>
        </div>

        <nav className={`admin-nav ${showMobileMenu ? 'mobile-menu-open' : ''}`} ref={mobileMenuRef}>
        <Link 
          to="/admin/center" 
          className={`nav-item ${location.pathname.includes('/admin/center') ? 'active' : ''}`}
        >
          <div className="nav-item-content">
            <LayoutDashboard className="nav-icon" size={18} />
            <span>Admin Center</span>
          </div>
          <span className="nav-item-badge" id="admin-center-badge" style={{ display: 'none' }}>0</span>
        </Link>
        <Link 
          to="/admin/analytics" 
          className={`nav-item ${location.pathname.includes('/admin/analytics') ? 'active' : ''}`}
        >
          <TrendingUp className="nav-icon" size={18} />
          <span>Analytics</span>
        </Link>
        <Link 
          to="/admin/orders" 
          className={`nav-item ${location.pathname.includes('/admin/orders') ? 'active' : ''}`}
        >
          <ClipboardList className="nav-icon" size={18} />
          <span>Orders</span>
        </Link>
        <Link 
          to="/admin/customers" 
          className={`nav-item ${location.pathname.includes('/admin/customers') ? 'active' : ''}`}
        >
          <Users className="nav-icon" size={18} />
          <span>{isSuperAdmin() ? 'Accounts' : 'Customers'}</span>
        </Link>
        <Link 
          to="/admin/promote" 
          className={`nav-item ${location.pathname.includes('/admin/promote') ? 'active' : ''}`}
        >
          <Megaphone className="nav-icon" size={18} />
          <span>Promote</span>
        </Link>
        <Link 
          to="/admin/products" 
          className={`nav-item ${location.pathname.includes('/admin/products') ? 'active' : ''}`}
        >
          <Package className="nav-icon" size={18} />
          <span>Products</span>
        </Link>
        <Link 
          to="/admin/inventory" 
          className={`nav-item ${location.pathname.includes('/admin/inventory') ? 'active' : ''}`}
        >
          <BarChart3 className="nav-icon" size={18} />
          <span>Inventory</span>
        </Link>
        </nav>

        <div className="admin-actions">
          <NotificationBell />
          <div className="profile-dropdown" ref={dropdownRef}>
            <div 
              ref={profileButtonRef}
              className="user-profile" 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowProfileDropdown(!showProfileDropdown);
              }}
              title={`Profile - ${user?.firstName || 'Admin'}`}
            >
              <UserCircle className="user-icon" size={24} />
              <ChevronDown className="dropdown-arrow" size={16} />
            </div>
            
            {showProfileDropdown && (
              <div className="profile-dropdown-menu">
                <Link 
                  to="/profile" 
                  className="dropdown-item"
                  onClick={() => setShowProfileDropdown(false)}
                >
                  <User className="dropdown-icon" size={18} />
                  Profile
                </Link>
                <div className="dropdown-divider"></div>
                <button 
                  onClick={handleLogout} 
                  className="dropdown-item logout-item"
                >
                  <LogOut className="dropdown-icon" size={18} />
                  LOGOUT
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Logout Confirmation Modal */}
      <ConfirmModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={confirmLogout}
        title="Logout"
        message="Are you sure you want to logout?"
        confirmText="Logout"
        cancelText="Cancel"
      />
    </header>
  );
};

export default AdminHeader; 